<script setup lang="ts">
  import { ref, type Component } from "vue";
  import { IconClose } from "../Icons";

  const inputRef = ref<HTMLInputElement>(null);

  const {
    label,
    value,
    modelValue,
    description,
    placeholder = " ",
    size = "m",
    variant = "primary",
    disabled,
    required,
    iconBefore,
    iconAfter,
    inputMode,
    type = "text",
  } = defineProps<{
    /** Метка для поля ввода */
    label?: string;
    /** Описание для поля ввода */
    description?: string;
    /** Значение для поля ввода */
    value?: string;
    /** Значение для поля ввода */
    modelValue?: string;
    /** Плейсхолдер для поля ввода */
    placeholder?: string;
    /** Размер поля ввода */
    size?: "s" | "m" | "l";
    /** Вариант поля ввода */
    variant?: "primary" | "secondary";
    /** Флаг для обязательного поля ввода */
    required?: boolean;
    /** Иконка перед поля ввода */
    iconBefore?: Component;
    /** Иконка после поля ввода */
    iconAfter?: Component;
    /** Флаг для отключения поля ввода */
    disabled?: boolean;
    /** Режим ввода для поля ввода */
    inputMode?: HTMLInputElement["inputMode"];
    /** Тип поля ввода */
    type?: HTMLInputElement["type"];
  }>();
  const emit = defineEmits<{
    /** Событие для обновления значения поля ввода */
    (e: "update:modelValue", value: string): void;
    /** Событие для обновления значения поля ввода */
    (e: "onchange", value: string): void;
  }>();

  defineExpose({ field: inputRef });

  const handleChange = (event: Event) => {
    // TODO: надо удалить emit('onchange', (event.target as HTMLInputElement).value);
    emit("onchange", (event.target as HTMLInputElement).value);
    emit("update:modelValue", (event.target as HTMLInputElement).value);
  };

  const handleClear = () => {
    // TODO: надо удалить emit('onchange', '')
    emit("onchange", "");
    emit("update:modelValue", "");
  };
</script>

<template>
  <div class="input">
    <label class="input__label" v-if="label">
      {{ label }}
      <span v-if="required" class="input__label-required">*</span>
    </label>
    <div class="input__wrapper">
      <component :is="iconBefore" v-if="iconBefore" class="input__icon input__icon--before" />
      <input
        ref="inputRef"
        :class="[
          'input__field',
          `input__field--size-${size}`,
          `input__field--${variant}`,
          { 'input__field--with-icon-before': iconBefore },
          { 'input__field--with-icon-after': iconAfter },
        ]"
        :type="type"
        :value="value || modelValue"
        :placeholder="placeholder"
        @input="handleChange"
        :disabled="disabled"
        :inputMode="inputMode"
      />
      <IconClose
        class="input__icon input__icon--clear"
        :style="{ right: iconAfter ? '48px' : '16px' }"
        @click="handleClear"
        data-testid="input-clear"
      />
      <component :is="iconAfter" v-if="iconAfter" class="input__icon input__icon--after" />
    </div>
    <span v-if="description" class="input__description">{{ description }}</span>
  </div>
</template>

<style scoped>
  .input {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .input__wrapper {
    position: relative;
  }

  .input__wrapper > svg {
    stroke: var(--sf-base-icon-secondary);
  }

  .input__label,
  .input__description {
    font-size: 14px;
    line-height: 20px;
    color: var(--sf-base-text-secondary);
  }

  .input__label-required {
    color: var(--sf-base-text-error);
  }

  .input__icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
  }

  .input__icon--before {
    left: 16px;
  }

  .input__icon--after {
    right: 16px;
  }

  .input__icon--clear {
    right: 16px;
    cursor: pointer;
    display: none;
  }

  .input__field {
    padding-left: 16px;
    padding-right: 16px;
    width: 100%;
    outline: none;
    text-overflow: ellipsis;
  }

  .input__field:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  .input__field::placeholder {
    color: var(--sf-base-text-tertiary);
  }

  .input__field:focus {
    border: 1px solid var(--sf-base-border-dark);
    background-color: var(--sf-base-background-secondary);
  }

  .input__wrapper:has(.input__icon--clear:hover) > .input__field:not(.input__field--with-icon-after):not(:placeholder-shown),
  .input__field:not(.input__field--with-icon-after):not(:placeholder-shown):hover,
  .input__field:not(.input__field--with-icon-after):not(:placeholder-shown):focus {
    padding-right: 48px;
  }

  .input__wrapper:has(.input__icon--clear:hover) > .input__field--with-icon-after,
  .input__field--with-icon-after:not(:placeholder-shown):hover,
  .input__field--with-icon-after:not(:placeholder-shown):focus {
    padding-right: 80px;
  }

  .input__field:not(:placeholder-shown) + .input__icon--clear:hover,
  .input__field:not(:placeholder-shown):focus + .input__icon--clear,
  .input__field:not(:placeholder-shown):hover + .input__icon--clear {
    display: initial;
  }

  .input__field--with-icon-before {
    padding-left: 48px;
  }

  .input__field--with-icon-after {
    padding-right: 48px;
  }

  .input__field--size-s {
    padding-top: 8px;
    padding-bottom: 8px;
    border-radius: 8px;
  }

  .input__field--size-m {
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 12px;
  }

  .input__field--size-l {
    padding-top: 12px;
    padding-bottom: 12px;
    border-radius: 12px;
  }

  .input__field--primary {
    background-color: var(--sf-input-primary-background-default);
    border: 1px solid transparent;
  }

  .input__wrapper:has(.input__icon--clear:hover) > .input__field--primary:not(:focus),
  .input__field--primary:hover {
    background-color: var(--sf-input-primary-background-hover);
    border-color: var(--sf-base-border-primary);
  }

  .input__field--primary:focus {
    background-color: var(--sf-input-primary-background-pressed);
    border-color: var(--sf-base-border-dark);
  }

  .input__field--secondary {
    background-color: var(--sf-input-secondary-background-default);
    border: 1px solid transparent;
  }

  .input__wrapper:has(.input__icon--clear:hover) > .input__field--secondary:not(:focus),
  .input__field--secondary:hover {
    background-color: var(--sf-input-secondary-background-hover);
    border: 1px solid var(--sf-base-border-primary);
  }

  .input__field--secondary:focus {
    background-color: var(--sf-input-secondary-background-pressed);
    border: 1px solid var(--sf-base-border-dark);
  }
</style>
